import React, { useEffect, useMemo } from 'react';
import {
	Link,
	Heading,
	TextBlock,
	MessageView, ExtendedTheme, Loader
} from 'neon-design-system';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { createStyles, makeStyles, Box } from '@material-ui/core';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { GET_PREMIUM_CHANNEL_BY_ID } from '../../../gql/channels';
import { stripePromise } from '../../../utils/stripe';

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) =>
	createStyles({
		root: {
			height: 'calc(100vh - 212px)',
			alignItems: 'center'
		},
		link: {
			fontSize: 'inherit'
		}
	})
);

const PremiumChannelSubscribeSuccessScreen = () => {
	const classes = useStyles();
	const history = useHistory();
	const { channelId, status } = useParams();
	const { search } = useLocation();
	const isSubscribed = status === 'subscribed';
	const isPayingInvoiceRequested = status === 'paying-invoice-requested';

	const { data, loading } = useQuery(GET_PREMIUM_CHANNEL_BY_ID, {
		variables: { id: channelId },
		fetchPolicy: 'cache-first'
	});

	const channel = data?.channel;

	const paymentIntentSecret = useMemo(() => {
		const params = new URLSearchParams(search);
		return params.get('payment_intent_client_secret');
	}, [search]);

	useEffect(() => {
		if (!paymentIntentSecret) return;

		stripePromise.then(stripe => {
			stripe?.retrievePaymentIntent(paymentIntentSecret).then(({paymentIntent}) => {
				let message = '';
				switch (paymentIntent?.status) {
				case 'succeeded':
					message = 'Congratulations! You\'re now subscribed and that is pretty cool.';
					break;

				case 'processing':
					message = 'Payment processing. We\'ll update you when payment is received.';
					break;

				case 'requires_payment_method':
					message = 'Payment failed. Please try another payment method.';
					break;

				default:
					message = 'Something went wrong.';
					break;
				}
				console.log(message);
			});
		});
	}, [paymentIntentSecret]);

	if (loading || !data) return <Loader />;

	const generateHeaderText = () => {
		if (isPayingInvoiceRequested) {
			return 'Thank you for submitting your subscription request'
		} else {
			return `You have successfully ${isSubscribed ? 'subscribed to' : 'unsubscribed from'} ${channel?.name}`;
		}
	}

	const generateSubheaderText = () => {
		if (isPayingInvoiceRequested) {
			return 'The Neon Concierge Team will finalise your subscription shortly.'
		} else {
			return isSubscribed ? (
				<>
					To see all your subscriptions, <Link to={baseUrls.subscriptions} className={classes.link}>manage your interests</Link>
				</>
			) : (
				<>
					If you unsubscribed by accident, you are welcome to resubscribe on the
					<Link to={baseUrls.channels.main} className={classes.link}> Browse </Link> page
				</>
			)
		}
	}

	return (
		<Box display="flex" className={classes.root}>
			<MessageView
				styleName="success"
				primaryAction={{
					label: isSubscribed || isPayingInvoiceRequested ? 'View Channel' : 'Browse Insights',
					onClick: () => history.push(isSubscribed || isPayingInvoiceRequested ? `${baseUrls.browse.channelMain}/${channelId}` : baseUrls.channels.main),
					styleName: 'success'
				}}
			>
				<Heading styleName="largeTitle" xs="small" md="medium" xl="large">{generateHeaderText()}</Heading>
				<Box mt={2}>
					<TextBlock styleName="body" color="grey" ink="muted">{generateSubheaderText()}</TextBlock>
				</Box>
			</MessageView>
		</Box>
	);
};

export default PremiumChannelSubscribeSuccessScreen;
